/**
 * ScheduleConfigStep - Wizard step 4 for schedule configuration
 *
 * Collects:
 * - Scan frequency (daily, hourly, every-15-min, continuous)
 * - Monitor type (scheduled, continuous, event-driven)
 * - Auto-ingest flag
 * - Advanced options: delay, batch size
 */

import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Info, Clock, Zap, Calendar } from 'lucide-react';
import { useMonitorCreationStore } from '@/features/monitors/store/monitorCreationStore';
import type { ScanFrequency, MonitorType } from '@/features/monitors/types/monitor.types';

/**
 * Scan frequency options with descriptions
 */
const SCAN_FREQUENCIES: Array<{ value: ScanFrequency; label: string; description: string }> = [
  {
    value: 'daily',
    label: 'Daily',
    description: 'Scan once per day at midnight',
  },
  {
    value: 'hourly',
    label: 'Hourly',
    description: 'Scan once per hour',
  },
  {
    value: 'every-15-min',
    label: 'Every 15 Minutes',
    description: 'Scan four times per hour',
  },
  {
    value: 'continuous',
    label: 'Continuous',
    description: 'Constantly monitor for changes',
  },
];

/**
 * Monitor type options with descriptions
 */
const MONITOR_TYPES: Array<{ value: MonitorType; label: string; description: string; icon: any }> = [
  {
    value: 'scheduled',
    label: 'Scheduled',
    description: 'Run scans on a fixed schedule',
    icon: Calendar,
  },
  {
    value: 'continuous',
    label: 'Continuous',
    description: 'Continuously poll for changes',
    icon: Zap,
  },
  {
    value: 'event-driven',
    label: 'Event-Driven',
    description: 'Respond to filesystem events in real-time',
    icon: Clock,
  },
];

/**
 * ScheduleConfigStep Component
 *
 * Fourth step of monitor creation wizard for schedule configuration.
 *
 * @example
 * ```tsx
 * <ScheduleConfigStep />
 * ```
 */
export const ScheduleConfigStep: React.FC = () => {
  const store = useMonitorCreationStore();

  return (
    <div className="space-y-6">
      {/* Scan Frequency Card */}
      <Card>
        <CardHeader>
          <CardTitle>Scan Frequency</CardTitle>
          <CardDescription>
            How often should the directory be scanned for new files?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="scan-frequency">
              Frequency <span className="text-destructive">*</span>
            </Label>
            <Select
              value={store.scan_frequency}
              onValueChange={(value) => store.setScanFrequency(value as ScanFrequency)}
            >
              <SelectTrigger id="scan-frequency">
                <SelectValue placeholder="Select scan frequency..." />
              </SelectTrigger>
              <SelectContent>
                {SCAN_FREQUENCIES.map((freq) => (
                  <SelectItem key={freq.value} value={freq.value}>
                    <div>
                      <div className="font-medium">{freq.label}</div>
                      <div className="text-xs text-muted-foreground">{freq.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {store.scan_frequency && (
              <p className="text-sm text-muted-foreground">
                {SCAN_FREQUENCIES.find((f) => f.value === store.scan_frequency)?.description}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Monitor Type Card */}
      <Card>
        <CardHeader>
          <CardTitle>Monitor Type</CardTitle>
          <CardDescription>
            How should the monitor detect file changes?
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="monitor-type">
              Type <span className="text-destructive">*</span>
            </Label>
            <Select
              value={store.monitor_type}
              onValueChange={(value) => store.setMonitorType(value as MonitorType)}
            >
              <SelectTrigger id="monitor-type">
                <SelectValue placeholder="Select monitor type..." />
              </SelectTrigger>
              <SelectContent>
                {MONITOR_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <type.icon className="h-4 w-4" />
                      <div>
                        <div className="font-medium">{type.label}</div>
                        <div className="text-xs text-muted-foreground">{type.description}</div>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {store.monitor_type && (
              <p className="text-sm text-muted-foreground">
                {MONITOR_TYPES.find((t) => t.value === store.monitor_type)?.description}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Auto-Ingest Card */}
      <Card>
        <CardHeader>
          <CardTitle>Ingestion Settings</CardTitle>
          <CardDescription>
            Configure automatic file ingestion behavior
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between space-x-2">
            <div className="space-y-0.5">
              <Label htmlFor="auto-ingest" className="text-base">
                Auto-Ingest Files
              </Label>
              <p className="text-sm text-muted-foreground">
                Automatically trigger ingestion for discovered files
              </p>
            </div>
            <Switch
              id="auto-ingest"
              checked={store.auto_ingest}
              onCheckedChange={store.setAutoIngest}
            />
          </div>

          {!store.auto_ingest && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Files will be detected but not automatically ingested. You'll need to manually trigger
                ingestion.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Advanced Options Card */}
      <Card>
        <CardHeader>
          <CardTitle>Advanced Options</CardTitle>
          <CardDescription>
            Fine-tune ingestion timing and batch processing
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Trigger Delay */}
          <div className="space-y-2">
            <Label htmlFor="trigger-delay">
              Trigger Delay (seconds)
            </Label>
            <Input
              id="trigger-delay"
              type="number"
              min={0}
              max={300}
              value={store.trigger_delay_seconds}
              onChange={(e) => store.setTriggerDelay(parseInt(e.target.value) || 5)}
            />
            <p className="text-sm text-muted-foreground">
              Wait time after file discovery before triggering ingestion (default: 5 seconds)
            </p>
          </div>

          {/* Batch Size */}
          <div className="space-y-2">
            <Label htmlFor="batch-size">
              Batch Size (files)
            </Label>
            <Input
              id="batch-size"
              type="number"
              min={1}
              max={100}
              value={store.batch_size}
              onChange={(e) => store.setBatchSize(parseInt(e.target.value) || 10)}
            />
            <p className="text-sm text-muted-foreground">
              Maximum number of files to process in a single batch (default: 10 files)
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Configuration Summary */}
      <Alert variant="info">
        <Info className="h-4 w-4" />
        <AlertTitle>Configuration Summary</AlertTitle>
        <AlertDescription>
          {store.scan_frequency && store.monitor_type ? (
            <div className="space-y-2">
              <p>
                This monitor will run as a <strong>{store.monitor_type}</strong> monitor, scanning{' '}
                <strong>{store.scan_frequency.replace('-', ' ')}</strong>.
              </p>
              {store.auto_ingest ? (
                <p>
                  Discovered files will be automatically ingested after a{' '}
                  <strong>{store.trigger_delay_seconds}s delay</strong>, processing up to{' '}
                  <strong>{store.batch_size} files</strong> at a time.
                </p>
              ) : (
                <p>
                  Discovered files will <strong>not</strong> be automatically ingested. Manual
                  ingestion will be required.
                </p>
              )}
            </div>
          ) : (
            <p>Please complete all required fields above to see the configuration summary.</p>
          )}
        </AlertDescription>
      </Alert>
    </div>
  );
};
